/**
 * 员工档案详情页
 * 表单形式展示和编辑员工详细信息
 * 包含: 基本信息、职位信息、薪酬社保、附件
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Form, Button, Space, Tabs, Avatar, message, Spin, Tag
} from 'antd';
import {
    UserOutlined, SaveOutlined, ArrowLeftOutlined,
    IdcardOutlined, TeamOutlined, DollarOutlined, PaperClipOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { memberAPI, departmentAPI, Member, Department } from '../../../services/organizationAPI';
import { MemberBasicInfoTab } from './MemberTabs/MemberBasicInfoTab';
import { MemberPositionTab } from './MemberTabs/MemberPositionTab';
import { MemberSalaryTab } from './MemberTabs/MemberSalaryTab';
import { MemberFilesTab } from './MemberTabs/MemberFilesTab';

const MemberDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [member, setMember] = useState<Member | null>(null);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [members, setMembers] = useState<Member[]>([]);

    // 加载数据
    useEffect(() => {
        const loadData = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const [memberRes, deptRes, membersRes] = await Promise.all([
                    memberAPI.get(parseInt(id)),
                    departmentAPI.list(),
                    memberAPI.list()
                ]);
                const memberData = memberRes.data;
                setMember(memberData);
                const deptData = deptRes.data as any;
                const membersData = membersRes.data as any;
                setDepartments(Array.isArray(deptData) ? deptData : deptData?.results || []);
                setMembers(Array.isArray(membersData) ? membersData : membersData?.results || []);

                // 设置表单值
                form.setFieldsValue({
                    ...memberData,
                    birth_date: memberData.birth_date ? dayjs(memberData.birth_date) : null,
                    hire_date: memberData.hire_date ? dayjs(memberData.hire_date) : null,
                    contract_end_date: memberData.contract_end_date ? dayjs(memberData.contract_end_date) : null,
                });
            } catch (error) {
                message.error('加载员工信息失败');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [id, form]);

    // 保存
    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            // 格式化日期
            const payload = {
                ...values,
                birth_date: values.birth_date?.format('YYYY-MM-DD'),
                hire_date: values.hire_date?.format('YYYY-MM-DD'),
                contract_end_date: values.contract_end_date?.format('YYYY-MM-DD'),
            };

            await memberAPI.update(parseInt(id!), payload);
            message.success('保存成功');
        } catch (error: any) {
            message.error(error.response?.data?.detail || '保存失败');
        } finally {
            setSaving(false);
        }
    };

    // Tab配置
    const tabItems = [
        {
            key: 'basic',
            label: <span><IdcardOutlined /> 基本信息</span>,
            children: <MemberBasicInfoTab member={member} />
        },
        {
            key: 'position',
            label: <span><TeamOutlined /> 职位信息</span>,
            children: <MemberPositionTab departments={departments} members={members} currentMemberId={id} />
        },
        {
            key: 'salary',
            label: <span><DollarOutlined /> 薪酬社保</span>,
            children: <MemberSalaryTab />
        },
        {
            key: 'files',
            label: <span><PaperClipOutlined /> 附件</span>,
            children: <MemberFilesTab />
        }
    ];

    if (loading) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    return (
        <div style={{ padding: 24 }}>
            <Card
                title={
                    <Space>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/organization')}>
                            返回
                        </Button>
                        <Avatar size={40} icon={<UserOutlined />} src={member?.avatar} />
                        <span>{member?.username} 的档案</span>
                        {member?.employee_status && (
                            <Tag color={member.employee_status === 'active' ? 'green' :
                                member.employee_status === 'probation' ? 'orange' : 'red'}>
                                {member.employee_status === 'active' ? '在职' :
                                    member.employee_status === 'probation' ? '试用期' : '离职'}
                            </Tag>
                        )}
                    </Space>
                }
                extra={
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                        保存
                    </Button>
                }
            >
                <Form form={form} layout="vertical" style={{ maxWidth: 800 }}>
                    <Tabs items={tabItems} />
                </Form>
            </Card>
        </div>
    );
};

export default MemberDetail;
